import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Space } from '../../models/space.model';
import { SpaceConfigOptionsService } from '../../services/space-config-options.service';
import { Solution } from '../../models/solution.model';
import { Edition } from '../../models/edition.model';
import { ProductDemo } from '../../models/product-demo.model';
import { WorkflowDemo } from '../../models/workflow-demo.model';
import { UseCase } from '../../models/use-case.model';
import { RoiResults } from '../../services/roi-calculator.service';
import { SolutionsService } from '../../services/solutions.service';
import { EditionsService } from '../../services/editions.service';
import { ProductDemosService } from '../../services/product-demos.service';
import { WorkflowDemosService } from '../../services/workflow-demos.service';
import { UseCasesService } from '../../services/use-cases.service';
import { RoiCalculatorService } from '../../services/roi-calculator.service';

export interface DemoItem {
  name: string;
  url: string;
  description?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF', SEK: 'kr',
  NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', AED: 'AED', INR: '₹'
};

@Component({
  selector: 'app-pdf-preview',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './pdf-preview.component.html',
  styleUrl: './pdf-preview.component.scss'
})
export class PdfPreviewComponent implements OnInit {
  @Input() space!: Space;

  readonly ready$ = new Subject<void>();

  selectedSolutions: Solution[] = [];
  selectedEditions: Edition[] = [];
  demoItems: DemoItem[] = [];
  useCases: UseCase[] = [];
  roiResults: RoiResults | null = null;
  solutionLogos = new Map<number, { icon: SafeHtml; name: SafeHtml; category: SafeHtml }>();

  exportDate = '';
  exportTime = '';

  constructor(
    private readonly solutionsService: SolutionsService,
    private readonly editionsService: EditionsService,
    private readonly productDemosService: ProductDemosService,
    private readonly workflowDemosService: WorkflowDemosService,
    private readonly useCasesService: UseCasesService,
    private readonly roiCalculatorService: RoiCalculatorService,
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer,
    private readonly translateService: TranslateService,
    private readonly spaceConfigOptions: SpaceConfigOptionsService
  ) {}

  ngOnInit(): void {
    const created = this.space.dateCreated ? new Date(this.space.dateCreated) : new Date();
    this.exportDate = created.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    this.exportTime = created.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    forkJoin({
      solutions: this.solutionsService.getSolutions(),
      editions: this.editionsService.getEditions(),
      productDemos: this.productDemosService.getProductDemos(),
      workflowDemos: this.workflowDemosService.getWorkflowDemos(),
      useCases: this.useCasesService.getUseCases()
    }).subscribe(({ solutions, editions, productDemos, workflowDemos, useCases }) => {
      const selectedNames = this.space.selectedSolutions ?? [];
      const selectedEditionIds = this.space.selectedEditions ?? [];

      this.selectedSolutions = solutions.filter(s => selectedNames.includes(s.name));
      this.selectedEditions = editions.filter(e => selectedEditionIds.includes(e.id));

      const bookmarkedPD = this.space.bookmarkedProductDemos ?? [];
      const bookmarkedWD = this.space.bookmarkedWorkflowDemos ?? [];
      const bookmarkedUC = this.space.bookmarkedUseCases ?? [];

      this.demoItems = [
        ...productDemos
          .filter(d => bookmarkedPD.includes(d.id))
          .map((d: ProductDemo) => ({ name: d.name, url: d.videoUrl, description: d.description })),
        ...workflowDemos
          .filter(d => bookmarkedWD.includes(d.id))
          .map((d: WorkflowDemo) => ({ name: d.name, url: d.videoUrl, description: d.description }))
      ];

      this.useCases = useCases.filter(uc => bookmarkedUC.includes(uc.id));

      this.roiResults = this.roiCalculatorService.calculateRoi({
        teamSize: this.space.teamSize,
        averageHourlyRate: this.space.averageHourlyRate,
        projectsPerYear: this.space.projectsPerYear,
        averageLicenceCost: this.space.averageLicenceCost,
        maturityLevel: this.space.maturityLevel ?? 20,
        productivityGainPercent: this.space.productivityGainPercent,
        reworkReductionPercent: this.space.reworkReductionPercent,
        coordinationSavingsPercent: this.space.coordinationSavingsPercent
      });

      const logoFetches = this.selectedSolutions
        .filter(s => s.logoIconUrl && s.logoNameUrl)
        .map(s => forkJoin({
          icon: this.http.get(s.logoIconUrl!, { responseType: 'text' }),
          name: this.http.get(s.logoNameUrl!, { responseType: 'text' }),
          category: s.logoCategoryUrl ? this.http.get(s.logoCategoryUrl, { responseType: 'text' }) : of(''),
        }).pipe(
          map(svgs => ({
            id: s.id,
            icon: this.sanitizer.bypassSecurityTrustHtml(this.addSvgSize(svgs.icon, 32, 32)),
            name: this.sanitizer.bypassSecurityTrustHtml(this.addSvgSize(svgs.name, 110, 16)),
            category: this.sanitizer.bypassSecurityTrustHtml(this.addSvgSize(svgs.category, 80, 13)),
          }))
        ));

      if (logoFetches.length === 0) {
        this.ready$.next();
      } else {
        forkJoin(logoFetches).subscribe(logos => {
          logos.forEach(l => this.solutionLogos.set(l.id, { icon: l.icon, name: l.name, category: l.category }));
          this.ready$.next();
        });
      }
    });
  }

  getIndustryName(id: string): string {
    return this.spaceConfigOptions.industries.find(i => i.id === id)?.name ?? id;
  }

  getIndustryDescription(id: string): string {
    return this.spaceConfigOptions.industries.find(i => i.id === id)?.description ?? '';
  }

  getIndustryImageUrl(id: string): string {
    return `assets/images/${id}.png`;
  }

  getDisciplineLabel(id: string): string {
    return this.spaceConfigOptions.disciplines.find(d => d.id === id)?.name ?? id;
  }

  getMaterialLabel(id: string): string {
    return this.spaceConfigOptions.materials.find(m => m.id === id)?.name ?? id;
  }

  formatEditionPrice(edition: Edition): string {
    const code = this.space.currencyCode ?? 'EUR';
    const amount = edition.prices?.[code] ?? edition.prices?.['EUR'];
    if (amount == null) return this.translateService.instant('PDF.EDITION.NOT_AVAILABLE');
    const symbol = CURRENCY_SYMBOLS[code] ?? code;
    const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${formatted} ${symbol} ${this.translateService.instant('PDF.EDITION.PER_MONTH')}`;
  }

  get currencySymbol(): string {
    return CURRENCY_SYMBOLS[this.space.currencyCode ?? 'EUR'] ?? (this.space.currencyCode ?? 'EUR');
  }

  get maturityFillPercent(): number {
    return this.space.maturityLevel ?? 20;
  }

  formatCurrency(value: number): string {
    return this.roiCalculatorService.formatCurrency(value);
  }

  get hasNotes(): boolean {
    return (this.space.notes?.length ?? 0) > 0;
  }

  private addSvgSize(svg: string, width: number, height: number): string {
    if (!svg) return '';
    return svg.replace(/<svg([^>]*)>/, (_match, attrs: string) => {
      const clean = attrs.replace(/\s*(width|height)="[^"]*"/g, '');
      return `<svg${clean} width="${width}" height="${height}">`;
    });
  }

  get hasDemos(): boolean {
    return this.demoItems.length > 0;
  }

  get hasUseCases(): boolean {
    return this.useCases.length > 0;
  }

  // Split arrays into rows of N for grid layout in template
  chunk<T>(arr: T[], size: number): T[][] {
    const result: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }
}
